import React, { useState, useEffect } from 'react';
import api from '../utils/api.js';
import { createLogger } from '../utils/logger.js';

const log = createLogger('QueueIndicator');

/**
 * QueueIndicator
 * Shows global queue status (e.g., "2 workflows in queue")
 * Polls every 30 seconds for updates
 */
export default function QueueIndicator({ className = '' }) {
    const [status, setStatus] = useState(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const data = await api.request('/api/v2/queue/status');
                if (data.success) {
                    setStatus(data);
                }
            } catch (err) {
                // Silently fail - not critical
                log.debug('Queue status fetch failed:', err.message);
            }
        };

        // Initial fetch
        fetchStatus();

        // Poll every 30 seconds
        const interval = setInterval(fetchStatus, 30000);

        return () => clearInterval(interval);
    }, []);

    // Don't show if no queue data or queue is empty
    if (!status || (status.queueLength === 0 && status.running < status.maxConcurrent)) {
        return null;
    }

    // Show busy indicator when at capacity or queue has items
    const isBusy = status.running >= status.maxConcurrent;
    const hasQueue = status.queueLength > 0;

    if (!isBusy && !hasQueue) {
        return null;
    }

    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${className} ${
            hasQueue
                ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300'
                : 'bg-blue-500/10 border border-blue-500/30 text-blue-300'
        }`}>
            <span className={`w-2 h-2 rounded-full ${hasQueue ? 'bg-amber-400' : 'bg-blue-400'} animate-pulse`} />
            {hasQueue ? (
                <span>{status.queueLength} in queue</span>
            ) : (
                <span>{status.running}/{status.maxConcurrent} running</span>
            )}
        </div>
    );
}
